
import Error from "@/components/Error.vue";
import PrettyBar from "@/components/PrettyBar.vue";
import NewSidebar from "@/components/NewSidebar.vue";
import useError from "@/composables/useError";
import {
  RoleAssigneeType,
  useGetUserQuery,
  useGetOboUserQuery,
  UserType,
  OrganizationType,
  EnrollmentStatus,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import {
  computed,
  defineComponent,
  reactive,
  ref,
  watch,
  toRef,
  nextTick,
  PropType,
  onMounted,
  inject,
} from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { required, email } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import useConfirmService from "@/composables/useConfirmService";
import useToastService from "@/composables/useToastService";
import useRandomPassword from "@/composables/useRandomPassword";
import usePasswordValidation from "@/composables/usePasswordValidation";
import { VDPopupListboxClass } from "@/components/pickers/VDPopupListbox";
import useDeleteUser from "./mutations/useDeleteUser";
import useCreateUser from "./mutations/useCreateUser";
import useUpdateUser from "./mutations/useUpdateUser";
import useMigrateUserToNonEidOrganization from "./mutations/useMigrateUserToNonEidOrganization";
import useResetUserAuthenticatorRegistration from "./mutations/useResetUserAuthenticatorRegistration";
import useUserCatalogSettings from "@/composables/useUserCatalogSettings";
import RolePicker from "./components/RolePicker.vue";
import UserRoleProfilePicker from "./components/UserRoleProfilePicker.vue";
import useAddUserRole from "./mutations/useAddUserRole";
import useRemoveUserRole from "./mutations/useRemoveUserRole";
import VDButton from "@/components/VDButton.vue";
import useUser from "@/composables/useUser";
import useUserSidebar from "./useUserSidebar";
import {
  displayUserCreationDate,
  totalDaysSinceLastLogin,
} from "@/composables/utils";
import {
  UKENDT_ORGANIZATION,
  UKENDT_ORGANIZATION_EU,
  UKENDT_ORGANIZATION_NON_EU,
} from "@/constants";
// Queries
gql`
  query getUser($id: ID) {
    user(userId: $id) {
      ...UserParts
      roles {
        roleId
        organizationId
        applicationId
        roleName
        organizationName
        applicationName
      }
      authenticatorRegistration {
        enrollmentStatus
      }
    }
  }
`;

gql`
  query getOboUser($id: ID) {
    user(userId: $id) {
      ...OboUserParts
      roles {
        roleId
        organizationId
        applicationId
        roleName
        organizationName
        applicationName
      }
    }
  }
`;

export default defineComponent({
  props: {
    organizationId: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    organization: {
      type: Object as PropType<OrganizationType>,
      required: true,
    },
  },
  components: {
    NewSidebar,
    PrettyBar,
    Error,
    RolePicker,
    UserRoleProfilePicker,
    VDButton,
  },
  emits: [
    "hide",
    "user-created",
    "user-updated",
    "user-deleted",
    "reload-tables",
  ],
  setup(props, { emit }) {
    const sidebarRef = ref();
    const { selectedUser } = useUserSidebar();
    const selectedUserOrgId = computed(() => {
      return (
        (selectedUser.value as any)?.organizationId ||
        selectedUser.value?.organization?.organizationId
      );
    });

    // Services
    const confirmService = useConfirmService("sidebar");
    const toastService = useToastService();
    const translate = inject<(key: string) => string>("$translate")!;

    // VDOM Refs
    const rolePicker = ref<VDPopupListboxClass>();
    const userRoleProfilePicker = ref<VDPopupListboxClass>();
    const firstnameInput = ref();

    // Properties
    const originalUser = ref<UserType>({});
    const editingUser = reactive<UserType>({ roles: [] });

    const { user } = useUser();
    const { mfaEnabled: userCatalogMfaEnabled } = useUserCatalogSettings();
    const canManageUsers = computed(
      () =>
        user.value?.isSuperAdmin ||
        user.value?.isAdmin ||
        user.value?.isApplicationAdmin ||
        false
    );
    const canAssignLocalAdminAndUpdateStatus = computed(
      () =>
        user.value?.isSuperAdmin ||
        user.value?.isAdmin ||
        user.value?.isApplicationAdmin ||
        false
    );

    const isForeignLocalAdminOnlyOfCurrentEditingUser = computed(() => {
      var editingUserOrgId = editingUser.organization?.organizationId;
      var currentLoginUserOrgId = user.value?.organizationId;
      return (
        isLocalAdminOnly.value && editingUserOrgId !== currentLoginUserOrgId
      );
    });

    const isLocalAdminOnly = computed(() => {
      return (
        !user.value?.isSuperAdmin &&
        !user.value?.isUserAdmin &&
        user.value?.isAdmin
      );
    });

    const canMigrateToNonEidOrganization = computed(
      () =>
        !isNewUser.value &&
        user.value?.isSuperAdmin &&
        !editingUser.mitID &&
        !editingUser.kombitID &&
        !editingUser.eID &&
        !(editingUser.organization?.vat === UKENDT_ORGANIZATION_NON_EU)
    );

    // Computed property to determine if role profile functionality should be available
    const canUseRoleProfiles = computed(() => {
      if (!isNewUser.value) {
        // For existing users, check their organization data
        return (
          editingUser.organization &&
          isNotUkendtOrganisation(editingUser.organization.vat)
        );
      } else {
        // For new users, use the passed organization prop (always available)
        return isNotUkendtOrganisation(props.organization.vat);
      }
    });

    // Computed property to get organization ID for role profile picker
    const roleProfileOrganizationId = computed(() => {
      if (!isNewUser.value) {
        return editingUser.organization?.organizationId || "";
      } else {
        return props.organizationId;
      }
    });

    // Computed role lists
    const userRoles = computed(() => {
      return (
        editingUser.roles?.filter(
          (role) =>
            role?.organizationId === editingUser.organization?.organizationId
        ) ?? []
      );
    });

    const onBehalfOfRoles = computed(() => {
      return (
        editingUser.roles?.filter(
          (role) =>
            role?.organizationId !== editingUser.organization?.organizationId
        ) ?? []
      );
    });

    const password = ref<string>(useRandomPassword());
    const isNewUser = computed(() => props.id == "new");
    const canResetAuthenticator = computed(
      () =>
        userCatalogMfaEnabled.value &&
        !isNewUser.value &&
        (canManageUsers.value || user.value?.isUserAdmin) &&
        !isForeignLocalAdminOnlyOfCurrentEditingUser.value &&
        editingUser.userId === props.id &&
        editingUser.authenticatorRegistration?.enrollmentStatus ===
          EnrollmentStatus.Enrolled
    );
    const isResettingAuthenticator = ref(false);

    const v$ = useVuelidate(useValidations(), {
      email: toRef(editingUser, "email"),
      firstname: toRef(editingUser, "firstname"),
      lastname: toRef(editingUser, "lastname"),
      password: password,
    });
    const hasConfirmedLeave = ref(false);
    const { result, loading, error, refetch } =
      isLocalAdminOnly.value &&
      selectedUserOrgId.value !== user.value?.organizationId
        ? useGetOboUserQuery(props, () => ({
            enabled: props.id != "new",
            fetchPolicy: "network-only",
          }))
        : useGetUserQuery(props, () => ({
            enabled: props.id != "new",
            fetchPolicy: "network-only",
          }));

    const deleteUserMutation = useDeleteUser(props.organizationId, props.id);
    const createUserMutation = useCreateUser(
      props.organizationId,
      editingUser,
      password
    );

    const updateUserMutation = useUpdateUser(
      props.organizationId,
      props.id,
      editingUser
    );

    // Separate mutation for checkbox updates that doesn't close the sidebar
    const updateUserStatusMutation = useUpdateUser(
      props.organizationId,
      props.id,
      editingUser
    );
    const addUserRole = useAddUserRole();
    const removeUserRole = useRemoveUserRole();
    const migrateUserToNonEidOrganizationMutation =
      useMigrateUserToNonEidOrganization(props.id);
    const resetUserAuthenticatorRegistrationMutation =
      useResetUserAuthenticatorRegistration();

    // Functions
    async function resetAuthenticatorRegistration() {
      if (
        !canResetAuthenticator.value ||
        isResettingAuthenticator.value ||
        loading.value
      ) {
        return;
      }

      const userId = props.id;
      isResettingAuthenticator.value = true;
      try {
        const response = await resetUserAuthenticatorRegistrationMutation.call(
          userId
        );
        if (response?.data?.resetUserAuthenticatorRegistration !== true) {
          toastService.show({
            severity: "error",
            title: translate("TextResources_ResetTwoFactorFailed"),
            message: translate("TextResources_ResetTwoFactorFailedDescription"),
          });
          return;
        }

        toastService.show({
          severity: "success",
          title: translate("TextResources_ResetTwoFactorSucceeded"),
          message: translate(
            "TextResources_ResetTwoFactorSucceededDescription"
          ),
        });

        if (props.id === userId) {
          // The reset succeeded even if the subsequent status refresh fails.
          editingUser.authenticatorRegistration = {
            ...editingUser.authenticatorRegistration,
            enrollmentStatus: EnrollmentStatus.NotEnrolled,
          };
          try {
            await refetch();
          } catch {
            toastService.show({
              severity: "warn",
              title: translate("TextResources_ResetTwoFactorRefreshFailed"),
              message: translate(
                "TextResources_ResetTwoFactorRefreshFailedDescription"
              ),
            });
          }
        }
      } catch {
        toastService.show({
          severity: "error",
          title: translate("TextResources_ResetTwoFactorFailed"),
          message: translate("TextResources_ResetTwoFactorRequestFailed"),
        });
      } finally {
        isResettingAuthenticator.value = false;
      }
    }

    function onAddedRole(role: RoleAssigneeType) {
      function add() {
        editingUser.roles = [...(editingUser.roles ?? []), role];
      }

      if (!isNewUser.value) {
        addUserRole
          .call(role.roleId ?? "", props.id, role.organizationId ?? "")
          .then((value) => {
            if (value != null) {
              add();
              toastService.show({
                severity: "success",
                title: "Rolle tildelt",
                message: `Rollen ${role.roleName} er blevet tildelt brugeren ${editingUser.email}`,
              });
              emit("reload-tables");
            }
          })
          .catch((error) => {
            let errorMessage = "Fejl ved tildeling af rolle";

            // Handle Apollo error structure
            if (error.message) {
              errorMessage = error.message;
            }

            // If there are specific GraphQL errors, use the first one
            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
              errorMessage = error.graphQLErrors[0].message;
            }

            toastService.show({
              severity: "error",
              title: "Fejl ved tildeling af rolle",
              message: errorMessage,
            });
          });
      } else {
        add();
      }
    }

    function onRemovedRole(role: RoleAssigneeType) {
      function remove() {
        editingUser.roles =
          editingUser.roles?.filter(
            (element) =>
              !(
                element?.roleId === role.roleId &&
                element?.organizationId === role.organizationId &&
                element?.applicationId === role.applicationId
              )
          ) ?? [];
      }

      if (!isNewUser.value) {
        removeUserRole
          .call(role.roleId ?? "", props.id, role.organizationId ?? "")
          .then((value) => {
            if (value != null) {
              remove();
              toastService.show({
                severity: "success",
                title: "Rolle fjernet",
                message: `Rollen ${role.roleName} er blevet fjernet fra brugeren ${editingUser.email}`,
              });
              emit("reload-tables");
            }
          });
      } else {
        remove();
      }
    }

    function onProfilesSelected(data: {
      profile: any;
      availableRoles: any[];
      unavailableRoles: any[];
    }) {
      const { profile, availableRoles, unavailableRoles } = data;

      if (availableRoles.length === 0 && unavailableRoles.length === 0) {
        toastService.show({
          severity: "info",
          title: "Ingen roller",
          message: `Profilen "${profile.name}" har ingen roller at tildele`,
        });
        return;
      }

      if (availableRoles.length === 0) {
        toastService.show({
          severity: "warn",
          title: "Ingen tilgængelige roller",
          message: `Profilen "${profile.name}" indeholder ${unavailableRoles.length} roller, men ingen af dem er tildelt organisationen og kan derfor ikke tildeles brugeren.`,
        });
        return;
      }

      // Filter out roles that are already assigned to the user
      const rolesToAdd = availableRoles.filter((role) => {
        return !editingUser.roles?.some(
          (userRole) => userRole?.roleId === role.roleId
        );
      });

      if (rolesToAdd.length === 0) {
        let message = `Alle tilgængelige roller fra profilen "${profile.name}" er allerede tildelt brugeren.`;
        if (unavailableRoles.length > 0) {
          message += ` ${unavailableRoles.length} roller kunne ikke tildeles da de ikke er tildelt organisationen.`;
        }
        toastService.show({
          severity: "info",
          title: "Ingen nye roller",
          message: message,
        });
        return;
      }

      // Add roles sequentially for existing users or directly for new users
      if (!isNewUser.value) {
        processUserRoleQueue(profile.name, rolesToAdd, unavailableRoles);
      } else {
        // For new users, add directly to the roles array
        rolesToAdd.forEach((role: any) => {
          const roleAssignee: RoleAssigneeType = {
            roleId: role.roleId,
            roleName: role.name,
            applicationName: role.application?.name || "Unknown Application",
            organizationId: editingUser.organization?.organizationId || "",
            organizationName: editingUser.organization?.name || "",
            isOnBehalf: false,
          };
          editingUser.roles = [...(editingUser.roles ?? []), roleAssignee];
        });

        // Show success message
        let message = `${rolesToAdd.length} roller fra profilen "${profile.name}" er tilføjet.`;
        if (unavailableRoles.length > 0) {
          message += ` ${unavailableRoles.length} roller kunne ikke tilføjes da de ikke er tildelt organisationen.`;
        }

        toastService.show({
          severity: unavailableRoles.length > 0 ? "warn" : "success",
          title: "Rolleprofil tilføjet",
          message: message,
        });
      }
    }

    async function processUserRoleQueue(
      profileName: string,
      rolesToAdd: any[],
      unavailableRoles: any[]
    ) {
      let addedCount = 0;
      let errorCount = 0;

      for (const role of rolesToAdd) {
        try {
          const roleAssignee: RoleAssigneeType = {
            roleId: role.roleId,
            roleName: role.name,
            applicationName: role.application?.name || "Unknown Application",
            organizationId: editingUser.organization?.organizationId || "",
            organizationName: editingUser.organization?.name || "",
            isOnBehalf: false,
          };

          await addUserRole.call(
            role.roleId,
            props.id,
            editingUser.organization?.organizationId || ""
          );

          // Add to local array on success
          editingUser.roles = [...(editingUser.roles ?? []), roleAssignee];
          addedCount++;
        } catch (error) {
          errorCount++;
          console.error("Failed to add role:", role.name, error);
        }
      }

      // Show result message
      let message = "";
      if (addedCount > 0) {
        message = `${addedCount} roller fra profilen "${profileName}" er tilføjet.`;
      }

      if (errorCount > 0) {
        message += ` ${errorCount} roller fejlede.`;
      }

      if (unavailableRoles.length > 0) {
        message += ` ${unavailableRoles.length} roller kunne ikke tilføjes da de ikke er tildelt organisationen.`;
      }

      const severity =
        errorCount > 0
          ? "warn"
          : unavailableRoles.length > 0
          ? "warn"
          : "success";
      const title =
        addedCount > 0 ? "Rolleprofil tilføjet" : "Fejl ved tilføjelse";

      toastService.show({
        severity: severity,
        title: title,
        message: message,
      });
    }

    function primaryButtonPressed() {
      v$.value.$touch();
      if (!v$.value.$error)
        isNewUser.value ? createUserMutation.call() : updateUserMutation.call();
    }

    function copyPasswordToClipboard() {
      if (password.value) navigator.clipboard.writeText(password.value);
    }

    function handleHideSidebar() {
      sidebarRef.value?.hide();
      emit("hide");
    }

    function show() {
      hasConfirmedLeave.value = false;
    }
    createUserMutation.onDone(({ data }) => {
      toastService.show({
        severity: "success",
        title: "Bruger oprettet",
        message: `${editingUser.email} er blevet oprettet, og har fået ID ${data?.createUser?.userId}`,
      });
      emit("user-created", data?.createUser);
      hasConfirmedLeave.value = true;
      handleHideSidebar();
    });

    createUserMutation.onError((error) => {
      toastService.show({
        severity: "error",
        error: useError(error),
      });
    });

    updateUserMutation.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Bruger opdateret",
        message: `${editingUser.email} er blevet opdateret`,
      });
      emit("user-updated", { userId: props.id, email: editingUser.email });
      hasConfirmedLeave.value = true;
      handleHideSidebar();
    });

    updateUserMutation.onError((error) => {
      toastService.show({
        severity: "error",
        error: useError(error),
      });
    });

    // Status update mutation callbacks (doesn't close sidebar)
    updateUserStatusMutation.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Status opdateret",
        message: `${editingUser.email} status er blevet opdateret`,
      });
      emit("user-updated", { userId: props.id, email: editingUser.email });
      // Don't close the sidebar for status updates
    });

    updateUserStatusMutation.onError((error) => {
      toastService.show({
        severity: "error",
        error: useError(error),
      });
    });

    deleteUserMutation.onDone(({ errors }) => {
      errors;
      toastService.show({
        severity: "success",
        title: "Bruger slettet",
        message: `${editingUser.email} med ID ${props.id} er blevet slettet!
          `,
      });
      emit("user-deleted", { userId: props.id, email: editingUser.email });
      hasConfirmedLeave.value = true;
      handleHideSidebar();
    });

    deleteUserMutation.onError((error) => {
      toastService.show({
        severity: "error",
        error: useError(error),
      });
    });

    migrateUserToNonEidOrganizationMutation.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Bruger flyttet",
        message: `${editingUser.email} er blevet flyttet til 'udenfor EU'`,
      });
      hasConfirmedLeave.value = true;
      handleHideSidebar();
      emit("reload-tables");
    });

    migrateUserToNonEidOrganizationMutation.onError((error) => {
      toastService.show({
        severity: "error",
        error: useError(error),
      });
    });

    watch(
      () => useResult(result).value,
      (newValue) => {
        originalUser.value = newValue ?? {};

        Object.assign(editingUser, newValue);
        editingUser.roles = [...(newValue?.roles ?? [])];
        nextTick(() => {
          sidebarRef.value?.show();
        });
      },
      { immediate: true }
    );

    // Initialize organization data for new users
    watch(
      () => props.organization,
      (organization) => {
        if (isNewUser.value && organization) {
          // Set organization data for new users using all properties from passed prop
          editingUser.organization = {
            ...organization,
            organizationId: organization.organizationId || "",
            name: organization.name || "",
            vat: organization.vat || "",
            assigneeRoles: organization.assigneeRoles || [],
          };
        }
      },
      { immediate: true }
    );

    // Watch for immediate updates on disabled checkbox
    watch(
      () => editingUser.disabled,
      (newValue, oldValue) => {
        if (
          !isNewUser.value &&
          oldValue !== undefined &&
          newValue !== oldValue
        ) {
          updateUserStatusMutation.call();
        }
      }
    );

    // Watch for immediate updates on administrator checkbox
    watch(
      () => editingUser.administrator,
      (newValue, oldValue) => {
        if (
          !isNewUser.value &&
          oldValue !== undefined &&
          newValue !== oldValue
        ) {
          updateUserStatusMutation.call();
        }
      }
    );

    onBeforeRouteLeave(() => {
      return true;
    });

    // Focus firstname input when component is ready
    const focusFirstnameInput = () => {
      nextTick(() => {
        setTimeout(() => {
          // Try multiple ways to access and focus the input
          const inputEl =
            firstnameInput.value?.$el?.querySelector("input") ||
            firstnameInput.value?.$el ||
            firstnameInput.value?.$refs?.input ||
            firstnameInput.value;

          if (inputEl && typeof inputEl.focus === "function") {
            inputEl.focus();
          }
        }, 100); // Small delay to ensure DOM is ready
      });
    };

    onMounted(() => {
      focusFirstnameInput();
    });

    // Also focus when user data is loaded for new users
    watch(
      () => editingUser.firstname,
      (newValue, oldValue) => {
        // Only focus if this is a new user or initial load
        if (
          isNewUser.value ||
          ((oldValue === undefined || oldValue === "") && newValue)
        ) {
          focusFirstnameInput();
        }
      }
    );

    function isNotUkendtOrganisation(vat: any): boolean {
      return (
        vat !== UKENDT_ORGANIZATION &&
        vat !== UKENDT_ORGANIZATION_EU &&
        vat !== UKENDT_ORGANIZATION_NON_EU
      );
    }

    return {
      EnrollmentStatus,
      rolePicker,
      userRoleProfilePicker,
      user: editingUser,
      userCatalogMfaEnabled,
      canManageUsers,
      canResetAuthenticator,
      isResettingAuthenticator,
      resetAuthenticatorRegistration,
      canAssignLocalAdminAndUpdateStatus,
      canUseRoleProfiles,
      isForeignLocalAdminOnlyOfCurrentEditingUser,
      isLocalAdminOnly,
      roleProfileOrganizationId,
      firstnameInput,
      userRoles,
      onBehalfOfRoles,
      password,
      v$,
      error: useError(error).value || undefined,
      loading,
      isNewUser,
      createUserMutation,
      updateUserMutation,
      deleteUserMutation,
      migrateUserToNonEidOrganizationMutation,
      canMigrateToNonEidOrganization,
      migrateToNonEidOrganization: () =>
        migrateUserToNonEidOrganizationMutation.call(),
      onAddedRole,
      onRemovedRole,
      onProfilesSelected,
      addRemoveLoading: computed(
        () => addUserRole.loading.value || removeUserRole.loading.value
      ),
      copyPasswordToClipboard,
      primaryButtonPressed,
      deleteUser: (event: Event) => {
        confirmService.show(
          event,
          "Er du sikker på at du vil slette brugeren? Denne handling kan ikke fortrydes",
          "Ja, slet",
          "Afbryd",
          () => deleteUserMutation.call()
        );
        const hasOboRoles = onBehalfOfRoles.value.length > 0;

        const message = `Er du sikker på, at du vil slette profilen? 
        Denne handling kan ikke fortrydes.`;

        const oboMessage = `Denne profil har tilknyttede "på-vegne-af roller".
        Hvis du sletter profilen, sletter du samtidig de tilknyttede roller.\n\n`;

        const anchor = event.currentTarget as HTMLElement;
        const ev = { currentTarget: anchor } as unknown as Event;

        const show = (msg: string, onAccept: () => void) =>
          confirmService.show(ev, msg, "Ja, slet", "Afbryd", onAccept);

        if (hasOboRoles) {
          show(message, async () => {
            await nextTick();
            show(oboMessage + message, () => {
              deleteUserMutation.call();
            });
          });
        } else {
          show(message, () => {
            deleteUserMutation.call();
          });
        }
      },
      handleHideSidebar,
      show,
      sidebarRef,
      totalDaysSinceLastLogin,
      displayUserCreationDate,
      selectedUser,
      isNotUkendtOrganisation,
    };
  },
});

function useValidations() {
  return {
    email: { email, required },
    firstname: { required },
    lastname: { required },
    password: usePasswordValidation(),
  };
}
